import React, { useState, useEffect } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./SopsInstanceExpandableTable.css";
import Sortdownarrow from "../../../../../Components/IconComponents/Sortdownarrow";
import Sortuparrow from "../../../../../Components/IconComponents/Sortuparrow";
import Sortingarrow from "../../../../../Components/IconComponents/Sortingarrow";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

/**
 * Default filter input for column filtering
 */
const DefaultColumnFilter = ({
  column: { filterValue, setFilter },
}) => (
  <input
    value={filterValue || ""}
    onChange={(e) => setFilter(e.target.value || undefined)}
    placeholder="Filter..."
    style={{
      width: "100%",
      padding: "5px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      marginTop: "5px",
    }}
  />
);

const SopsInstanceExpandableTable = ({
  columns,
  data,
  numberOfRows,
  headerTableHeadStyle = {},
  tableStyle = {},
  showRowCount = true,
  rowCount,
  setSearchData = () => {},
  enableColumnFilters = false,
  disbalePaginationFilter = false,
  disbalePaginationFilter1 = false,
}) => {
  const { t } = useTranslation();
  const [expandedRowIds, setExpandedRowIds] = useState({});

  // Toggle expand/collapse for a row
  const toggleRowExpansion = (rowId) => {
    setExpandedRowIds((prev) => {
      const isExpanded = !!prev[rowId];
      return isExpanded ? {} : { [rowId]: true };
    });
  };

  // Table instance from react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // For number translation
  const translateNumber = (number) => {
    const numStr = number.toString();
    return i18n.t(`numbers.${numStr}`, { defaultValue: numStr });
  };

  // Row count calculations
  const totalRows = globalFilter ? rows.length : rowCount || data.length;
  const startIndex = pageIndex * pageSize + 1;
  const endIndex = Math.min((pageIndex + 1) * pageSize, totalRows);

  // Set initial page size when numberOfRows changes
  useEffect(() => {
    setPageSize(numberOfRows);
  }, [numberOfRows, setPageSize]);

  // Update search data whenever filtering changes
  useEffect(() => {
    const pageData = (globalFilter ? page : rows)?.map((r) => r.original);
    setSearchData(pageData || []);
  }, [globalFilter, rows, page, setSearchData]);

  /**
   * Expanded row renderer — displays the sub-table
   */
  const renderExpandedRow = (row) => {
    const userTaskMap = row.original.userTaskMapList || [];
    const parentTaskType = row.original.taskType;

    return (
      <tr>
        <td colSpan="6">
          <div style={{ width: "85%", borderRadius: "10px", margin: "10px 0 10px 80px" }}>
            <table style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#1a7a73", color: "white" }}>
                  {["Assign To", "Task Name", "Updated On", "Action Message", "Status", "Priority"]
                    .map((header) => (
                      <th key={header} style={{ padding: "8px", textAlign: "left" }}>
                        {t(header)}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {userTaskMap.map((task) => (
                  <tr key={task.id}>
                    {/* Assign To */}
                    <td style={{ padding: "10px" }}>{task.user_name || t("NA")}</td>

                    {/* Task Name */}
                    <td style={{ padding: "10px" }}>{task.task_name || t("NA")}</td>

                    {/* Updated On */}
                    <td style={{ padding: "8px" }}>
                      {task.updated_on || task.created_on
                        ? new Date(task.updated_on || task.created_on).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : t("NA")}
                    </td>

                    {/* Action Message */}
                    <td style={{ padding: "10px" }}>{task.description || t("NA")}</td>

                    {/* Status */}
                    <td style={{ padding: "10px" }}>
                      {(() => {
                        const statusMap = {
                          TASK_EXE_STATUS_NEW_TASK: {
                            color: "#B2EAFE",
                            label:
                              parentTaskType === "341" || task.parent_task_type === "341"
                                ? t("Started")
                                : parentTaskType === "340" || task.parent_task_type === "340"
                                ? t("New Message")
                                : t("Started"),
                          },
                          TASK_EXE_STATUS_INPROGRESS: { color: "#fccd7c", label: t("In Progress") },
                          TASK_EXE_STATUS_COMPLETED: {
                            color: "#7cdb90",
                            label:
                              parentTaskType === "341" || task.parent_task_type === "341"
                                ? t("Completed")
                                : parentTaskType === "340" || task.parent_task_type === "340"
                                ? t("Acknowledged")
                                : t("Completed"),
                          },
                          TASK_EXE_STATUS_CANCEL: { color: "#e95060", label: t("Canceled") },
                          TASK_EXE_STATUS_CLOSED: { color: "Gray", label: t("Closed") },
                        };
                        const status = statusMap[task.status_code];
                        return status ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                backgroundColor: status.color,
                                borderRadius: "50%",
                              }}
                            ></span>
                            {status.label}
                          </div>
                        ) : (
                          task.status_name || t("NA")
                        );
                      })()}
                    </td>

                    {/* Priority */}
                    <td style={{ padding: "10px" }}>
                      {(() => {
                        const priorityMap = {
                          0: { color: "#b2eafe", label: t("Low") },
                          1: { color: "#fccd7c", label: t("Medium") },
                          2: { color: "#e87c86", label: t("High") },
                        };
                        const priority = priorityMap[task.priority] || priorityMap[0];
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                backgroundColor: priority.color,
                                borderRadius: "50%",
                              }}
                            ></span>
                            {priority.label}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="tabl_container">
      {/* Table Title */}
      <div
        style={{
          marginLeft: "15px",
          marginTop: "1rem",
          marginBottom: "1rem",
          color: "#1a7a73",
          fontSize: "17px",
          fontWeight: "700",
        }}
      >
        Task List
      </div>

      {/* Main Table */}
      <div className="iccc__table--container" style={tableStyle}>
        <table {...getTableProps()} className="table iccc--table">
          <thead style={{ backgroundColor: "#1a7a73" }}>
            <tr>
              {/* Expand Column */}
              <th style={{ width: "30px", color: "white", backgroundColor: "#1a7a73" }}></th>

              {/* Column Headers */}
              {headerGroups.map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={headerTableHeadStyle}
                      key={column.id}
                    >
                      {column.render("Header")}
                      {!column.disableSortBy &&
                        (column.isSorted ? (
                          column.isSortedDesc ? <Sortdownarrow /> : <Sortuparrow />
                        ) : (
                          <Sortingarrow />
                        ))}
                      {enableColumnFilters && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row) => {
                prepareRow(row);
                const isExpanded = expandedRowIds[row.id];
                return (
                  <React.Fragment key={row.id}>
                    <tr {...row.getRowProps()}>
                      {/* Expand/Collapse Icon */}
                      <td style={{ textAlign: "left" }}>
                        <span
                          onClick={() => toggleRowExpansion(row.id)}
                          style={{ cursor: "pointer", fontSize: "18px" }}
                        >
                          {isExpanded ? "-" : "+"}
                        </span>
                      </td>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} key={cell.column.id} style={{ textAlign: "left" }}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && renderExpandedRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="no-data-text">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        {showRowCount && (
          <div className="float-start m-2 mt-1 ms-3 pagecount-style">
            {t("showingentriestable.showing")} {translateNumber(startIndex)}{" "}
            {t("showingentriestable.to")} {translateNumber(endIndex)}{" "}
            {t("showingentriestable.of")} {translateNumber(totalRows)}{" "}
            {t("showingentriestable.entries")}
          </div>
        )}

        <div className="pagination">
          {/* Go to Page & Page Size */}
          {!disbalePaginationFilter && (
            <div className="d-flex">
              <span className="pagination_gotopage">
                Go to page:
                <input
                  type="number"
                  min="1"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    let pageNum = Number(e.target.value) || 1;
                    gotoPage(pageNum - 1);
                  }}
                  className="pagination_gotopage-input"
                />
              </span>
              <span>
                <select
                  className="pagination_select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 50, 100, 500, 1000, 2000].map((size) => (
                    <option key={size} value={size}>
                      show {size}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          )}

          {/* Prev / Next */}
          {!disbalePaginationFilter1 && (
            <>
              <button className="pagebtn mx-2" onClick={previousPage} disabled={!canPreviousPage}>
                {"<"}
              </button>
              <button className="pagination_page">{translateNumber(pageIndex + 1)}</button>
              <button className="pagebtn" onClick={nextPage} disabled={!canNextPage}>
                {">"}
              </button>
              <span className="right_pageno">
                {t("pagination.page_of", {
                  page: translateNumber(pageIndex + 1),
                  total: translateNumber(pageOptions.length),
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SopsInstanceExpandableTable;
